import { Box, Tooltip } from "@mui/material";
import { type ObjectType, type WorkflowStatus } from "store/appData";
import { type DefectStatus, type ImageAnnotation } from "../api";

const DEFAULT_BORDER_WIDTH = 2;

// A non-colour cue for the same distinction as the defect-status colour —
// dash pattern differs by status so it still reads under colourblindness.
// False positive also gets a thinner outline so it reads as distinct from
// a solid, non-hovered object-dataset box (which uses DEFAULT_BORDER_WIDTH).
const DEFECT_BOX_BORDER: Record<DefectStatus, { style: string; width: number }> = {
  falsePositive: { style: "solid", width: 1 },
  issue: { style: "dashed", width: DEFAULT_BORDER_WIDTH },
  clean: { style: "dotted", width: DEFAULT_BORDER_WIDTH },
};

// `annotations` must be pre-filtered with the displayable type first; `box` is
// the rendered image rect so boxes align with the parent's zoom/pan.
export default function AnnotationOverlay({
  annotations,
  objectTypes,
  workflowStatuses,
  getAnnotationColor,
  box,
  hoveredId,
}: {
  annotations: ImageAnnotation[];
  objectTypes: Record<number, ObjectType>;
  workflowStatuses: Record<number, WorkflowStatus>;
  getAnnotationColor: (annotation: ImageAnnotation) => {
    color: string;
    defectStatus?: DefectStatus;
  };
  box: { x: number; y: number; width: number; height: number };
  hoveredId: number | null;
}) {
  return (
    <Box
      data-testid="datasetImagePanel.annotationOverlay"
      sx={{
        position: "absolute",
        left: box.x,
        top: box.y,
        width: box.width,
        height: box.height,
        pointerEvents: "none",
      }}
    >
      {annotations.map((annotation) => {
        const { color, defectStatus } = getAnnotationColor(annotation);
        const hovered = annotation.id === hoveredId;
        // Defect boxes use their status's dash pattern rather than toggling
        // dashed/solid on hover, so the pattern keeps signalling the status;
        // hover is shown as a glow around the border instead.
        const { style: borderStyle, width: borderWidth } = defectStatus
          ? DEFECT_BOX_BORDER[defectStatus]
          : { style: hovered ? "solid" : "dashed", width: DEFAULT_BORDER_WIDTH };
        return (
          <Tooltip
            key={annotation.id}
            title={
              <>
                {annotation.types.map((annotationType, index) => {
                  const objectType = objectTypes[annotationType.object_type_id];
                  const workflowStatusName =
                    annotationType.workflow_status != null
                      ? workflowStatuses[annotationType.workflow_status]?.name
                      : undefined;
                  return (
                    <Box key={index}>
                      <Box>{objectType.en_name}</Box>
                      {workflowStatusName && (
                        <Box
                          data-testid={`datasetImagePanel.annotationWorkflowStatus.${annotation.id}.${index}`}
                          sx={{ fontSize: "0.85em", opacity: 0.7 }}
                        >
                          {workflowStatusName}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </>
            }
            open={hovered}
            placement="top"
            arrow
            disableHoverListener
            disableFocusListener
            disableTouchListener
          >
            <Box
              data-testid={`datasetImagePanel.annotation.${annotation.id}`}
              data-defect-status={defectStatus}
              sx={{
                position: "absolute",
                left: `${100 * annotation.x}%`,
                top: `${100 * annotation.y}%`,
                width: `${100 * annotation.width}%`,
                height: `${100 * annotation.height}%`,
                border: `${borderWidth}px ${borderStyle} ${color}`,
                boxShadow:
                  defectStatus && hovered ? `0 0 0 2px ${color}` : undefined,
                boxSizing: "border-box",
                pointerEvents: "none",
                zIndex: hovered ? 1 : undefined,
                transform: annotation.rotation
                  ? `rotate(${annotation.rotation}deg)`
                  : undefined,
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
}
